import React, { useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import Table from "../../components/Table";
import FormInput from "../../components/FormInput";
import "../../containers/Dashboard/Dashboard.css";
import "./logs.css";
import { getData, GET_NEW_USERS_LOGS_URL, GET_LOOKUP_SUBSCRIPTIONS_LOGS_URL } from "../../utils/api";

import HeaderIconSvg from "../../icons/newicons/arrow-down-up.svg";

let TIMER = null;

const logUserRowDetailId = (user) => {
  const candidates = [user.userId, user.id, user._id];
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return v;
    }
  }
  return null;
};

const todayRange = () => {
  const startDt = new Date().setHours(0, 0, 0, 0);
  const endDt = new Date().setHours(23, 59, 59, 999);
  return { startDt, endDt };
};

const parseLogsSearch = (search) => {
  const q = new URLSearchParams(search || "");
  const tab = q.get("tab") === "lookup-subscriptions" ? "lookup-subscriptions" : "new-users";
  const limit = Math.max(1, parseInt(q.get("limit"), 10) || 10);
  const offset = Math.max(0, parseInt(q.get("offset"), 10) || 0);
  const text = q.get("text") || "";
  const kycRaw = q.get("kyc");
  const accountRaw = q.get("account");
  const kyc = kycRaw === "1" || kycRaw === "2" ? Number(kycRaw) : null;
  const account = accountRaw === "1" || accountRaw === "2" ? Number(accountRaw) : null;
  return { tab, limit, offset, text, kyc, account };
};

const buildLogsSearch = ({ tab, limit, offset, text, kyc, account }) => {
  const q = new URLSearchParams();
  if (tab && tab !== "new-users") q.set("tab", tab);
  if (limit && limit !== 10) q.set("limit", String(limit));
  if (offset) q.set("offset", String(offset));
  if (text) q.set("text", text);
  if (kyc === 1 || kyc === 2) q.set("kyc", String(kyc));
  if (account === 1 || account === 2) q.set("account", String(account));
  const s = q.toString();
  return s ? `?${s}` : "";
};

export const Logs = () => {
  const history = useHistory();
  const location = useLocation();
  const initial = parseLogsSearch(location.search);

  const [activeTab, setActiveTab] = useState(initial.tab);
  const [searchValue, setSearchValue] = useState(initial.text);
  const [newUsersList, setNewUsersList] = useState([]);
  const [lookupSubscriptionsList, setLookupSubscriptionsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState({
    limit: initial.limit,
    offset: initial.offset,
    areMoreItemsAvailable: false,
    text: initial.text,
    kyc: initial.kyc,
    account: initial.account,
  });

  // Keep list filters/pagination in the URL so browser back (and Back to Logs) restore them
  useEffect(() => {
    const nextSearch = buildLogsSearch({
      tab: activeTab,
      limit: params.limit,
      offset: params.offset,
      text: params.text,
      kyc: params.kyc,
      account: params.account,
    });
    const currentSearch = location.search || "";
    if (currentSearch !== nextSearch) {
      history.replace(`/config/logs${nextSearch}`);
    }
  }, [activeTab, params.limit, params.offset, params.text, params.kyc, params.account]);

  const logsListSearch = buildLogsSearch({
    tab: activeTab,
    limit: params.limit,
    offset: params.offset,
    text: params.text,
    kyc: params.kyc,
    account: params.account,
  });

  const getNewUsers = async () => {
    try {
      setIsLoading(true);
      const { startDt, endDt } = todayRange();

      const query = {
        count: params.limit,
        offset: params.offset,
        text: params.text || "",
        from: startDt,
        to: endDt,
      };
      if (params.kyc === 1 || params.kyc === 2) {
        query.kyc = params.kyc;
      }
      if (params.account === 1 || params.account === 2) {
        query.account = params.account;
      }

      const data = await getData(GET_NEW_USERS_LOGS_URL, query);

      setIsLoading(false);

      const mappedData = (data.list || []).map((user) => {
        const detailId = logUserRowDetailId(user);
        return {
          ...user,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
          address: user.address
            ? `${user.address}${user.city ? `, ${user.city}` : ""}${user.state ? `, ${user.state}` : ""}${user.pincode ? ` ${user.pincode}` : ""}`.trim()
            : "",
          joinedDate: user.createdDate
            ? new Date(user.createdDate).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })
            : "",
          viewDetails: detailId ? (
            <Link
              to={{
                pathname: `/config/logs/user/${encodeURIComponent(String(detailId))}`,
                state: { logsSearch: logsListSearch },
              }}
              onClick={(e) => e.stopPropagation()}
              className="logs-page__view-link"
            >
              View details
            </Link>
          ) : (
            <span className="c-gray fs-12">—</span>
          ),
        };
      });

      setNewUsersList(mappedData);
      setParams((prev) => ({
        ...prev,
        areMoreItemsAvailable: data.areMoreItemsAvailable || false,
      }));
    } catch (e) {
      console.log("ERROR", e);
      setIsLoading(false);
      setNewUsersList([]);
    }
  };

  const getLookupSubscriptions = async () => {
    try {
      setIsLoading(true);
      const { startDt, endDt } = todayRange();

      const data = await getData(GET_LOOKUP_SUBSCRIPTIONS_LOGS_URL, {
        count: params.limit,
        offset: params.offset,
        text: params.text || "",
        from: startDt,
        to: endDt,
      });

      setIsLoading(false);

      const mappedData = (data.list || []).map((subscription) => ({
        ...subscription,
        companyName: subscription.companyName || "",
        username: subscription.username || "",
        planName: subscription.planName || "",
        price: subscription.originalPrice && subscription.finalPrice
          ? `$${subscription.originalPrice} / $${subscription.finalPrice}`
          : subscription.originalPrice
          ? `$${subscription.originalPrice}`
          : subscription.finalPrice
          ? `$${subscription.finalPrice}`
          : "",
        amountCaptured: subscription.amount ? `$${(subscription.amount / 100).toFixed(2)}` : "",
        date: subscription.created
          ? new Date(subscription.created).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })
          : "",
      }));

      setLookupSubscriptionsList(mappedData);
      setParams((prev) => ({
        ...prev,
        areMoreItemsAvailable: data.areMoreItemsAvailable || false,
      }));
    } catch (e) {
      console.log("ERROR", e);
      setIsLoading(false);
      setLookupSubscriptionsList([]);
    }
  };

  useEffect(() => {
    if (activeTab === "new-users") {
      getNewUsers();
    } else if (activeTab === "lookup-subscriptions") {
      getLookupSubscriptions();
    }
  }, [params.offset, params.limit, params.text, params.kyc, params.account, activeTab]);

  const setKycFilter = (value) => {
    setParams((prev) => ({
      ...prev,
      kyc: value,
      offset: 0,
    }));
  };

  const setAccountFilter = (value) => {
    setParams((prev) => ({
      ...prev,
      account: value,
      offset: 0,
    }));
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setParams((prev) => ({
      ...prev,
      offset: 0,
    }));
  };

  const fetchMore = () => {
    setParams((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const fetchLess = () => {
    setParams((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    if (TIMER) {
      clearTimeout(TIMER);
    }
    TIMER = setTimeout(() => {
      setParams((prev) => ({
        ...prev,
        text: val,
        offset: 0,
      }));
    }, 500);
  };

  return (
    <PageLayout icon={HeaderIconSvg} heading="Logs" hideSearchBar={true}>
      <div className="logs-page__toolbar">
        <div className="logs-page__toolbar-left">
          <div className="logs-page__tabs groups-tabs">
            <div className="tabs flex flex-middle logs-page__tabs-inner">
              <div
                className={`ht-40 flex flex-middle pl-16 pr-16 fw-bolder c-pointer ${
                  activeTab === "new-users" ? "bg-white c-gray" : "bg-lt-gray-2 c-white"
                }`}
                onClick={() => switchTab("new-users")}
              >
                <span>New Users</span>
              </div>
              <div
                className={`ht-40 flex flex-middle pl-16 pr-16 fw-bolder c-pointer ${
                  activeTab === "lookup-subscriptions" ? "bg-white c-gray" : "bg-lt-gray-2 c-white"
                }`}
                onClick={() => switchTab("lookup-subscriptions")}
              >
                <span>Lookup Subscriptions</span>
              </div>
              {activeTab === "new-users" && (
                <>
                  <span className="logs-page__tab-divider" aria-hidden="true" />
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="KYC: all"
                    className={`ht-40 flex flex-middle pl-16 pr-16 fw-bolder c-pointer ${
                      params.kyc == null ? "bg-white c-gray" : "bg-lt-gray-2 c-white"
                    }`}
                    onClick={() => setKycFilter(null)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setKycFilter(null);
                      }
                    }}
                  >
                    <span>All</span>
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`ht-40 flex flex-middle pl-16 pr-16 fw-bolder c-pointer ${
                      params.kyc === 1 ? "bg-white c-gray" : "bg-lt-gray-2 c-white"
                    }`}
                    onClick={() => setKycFilter(1)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setKycFilter(1);
                      }
                    }}
                  >
                    <span>KYC Filled</span>
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`ht-40 flex flex-middle pl-16 pr-16 fw-bolder c-pointer ${
                      params.kyc === 2 ? "bg-white c-gray" : "bg-lt-gray-2 c-white"
                    }`}
                    onClick={() => setKycFilter(2)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setKycFilter(2);
                      }
                    }}
                  >
                    <span>KYC Pending</span>
                  </div>
                  <span className="logs-page__tab-divider" aria-hidden="true" />
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Account: all"
                    className={`ht-40 flex flex-middle pl-16 pr-16 fw-bolder c-pointer ${
                      params.account == null ? "bg-white c-gray" : "bg-lt-gray-2 c-white"
                    }`}
                    onClick={() => setAccountFilter(null)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setAccountFilter(null);
                      }
                    }}
                  >
                    <span>All</span>
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`ht-40 flex flex-middle pl-16 pr-16 fw-bolder c-pointer ${
                      params.account === 1 ? "bg-white c-gray" : "bg-lt-gray-2 c-white"
                    }`}
                    onClick={() => setAccountFilter(1)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setAccountFilter(1);
                      }
                    }}
                  >
                    <span>Account approved</span>
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`ht-40 flex flex-middle pl-16 pr-16 fw-bolder c-pointer ${
                      params.account === 2 ? "bg-white c-gray" : "bg-lt-gray-2 c-white"
                    }`}
                    onClick={() => setAccountFilter(2)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setAccountFilter(2);
                      }
                    }}
                  >
                    <span>Account pending</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="logs-page__toolbar-search">
          <div className="logs-search-wrapper">
            <FormInput
              className="top_form_input"
              inputType="search"
              value={searchValue}
              onChange={handleSearchChange}
              placeholder="Name, email, phone…"
              inputClassName="form-control"
            />
          </div>
        </div>
      </div>

      <div className="logs-page__table-wrap">
        {activeTab === "new-users" && (
          <div>
            <Table
              scrollTable={false}
              columns={["First name", "Last name", "Email", "Phone", "Address", "Joined", "Actions"]}
              items={newUsersList}
              keys={["firstName", "lastName", "email", "phone", "address", "joinedDate", "viewDetails"]}
              widthClasses={["wt-120", "wt-120", "flex-1", "wt-130", "flex-1", "wt-110", "wt-140"]}
              hideCheckBox={true}
              isLoading={isLoading}
              offset={params.offset}
              areMoreItemsAvailable={params.areMoreItemsAvailable}
              fetchMore={fetchMore}
              fetchLess={fetchLess}
              onCountChange={(value) => {
                setParams((prev) => ({
                  ...prev,
                  offset: 0,
                  limit: value,
                }));
              }}
              limit={params.limit}
            />
          </div>
        )}

        {activeTab === "lookup-subscriptions" && (
          <div>
            <Table
              scrollTable={false}
              columns={["Company Name", "Username", "Plan Name", "Original Price / Final Price", "Amount Captured", "Date"]}
              items={lookupSubscriptionsList}
              keys={["companyName", "username", "planName", "price", "amountCaptured", "date"]}
              widthClasses={["flex-1", "flex-1", "flex-1", "flex-1", "flex-1", "flex-1"]}
              hideCheckBox={true}
              isLoading={isLoading}
              offset={params.offset}
              areMoreItemsAvailable={params.areMoreItemsAvailable}
              fetchMore={fetchMore}
              fetchLess={fetchLess}
              onCountChange={(value) => {
                setParams((prev) => ({
                  ...prev,
                  offset: 0,
                  limit: value,
                }));
              }}
              limit={params.limit}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default Logs;
